import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Check } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { StepProgress } from "@/components/fionova/StepProgress";
import { EllieBridge } from "@/components/journey/EllieBridge";
import { PrimaryButton, Eyebrow } from "@/components/fionova/Primitives";
import { generatePlan } from "@/services/unsaidService";
import { unsaidStore, transcriptHash } from "@/lib/unsaidStore";
import { trackSessionOnce } from "@/lib/analytics";

export default function TodayPlan() {
  const navigate = useNavigate();
  const [outcome] = useState(() => unsaidStore.getOutcome());
  const [xrayOutcome] = useState(() => unsaidStore.getXrayOutcome());
  const [status, setStatus] = useState("loading"); // loading | reveal | error | missing
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState({});
  const startedRef = useRef(false);

  const hasHandoff = !!(outcome && (outcome.transcript || []).some((m) => m.role === "user"));

  const load = useCallback(async () => {
    if (!hasHandoff) { setStatus("missing"); return; }
    const hash = transcriptHash(outcome.transcript);
    const storedOutcome = unsaidStore.getPlanOutcome();
    const fallback = storedOutcome && storedOutcome.hash === hash ? storedOutcome.plan : null;
    const cached = unsaidStore.getPlanCache(hash) || fallback;
    if (cached) { unsaidStore.setPlanCache(hash, cached); setPlan((p) => p || cached); setStatus((s) => (s === "reveal" ? s : "reveal")); return; }
    setStatus((s) => (s === "reveal" ? s : "loading"));
    const result = await generatePlan(outcome, xrayOutcome?.xray || {});
    if (!result.ok) { setStatus((s) => (s === "reveal" ? s : "error")); return; }
    unsaidStore.setPlanCache(hash, result.plan);
    unsaidStore.savePlanOutcome({ plan: result.plan, hash, fromXrayAt: xrayOutcome?.savedAt || null });
    setPlan((p) => p || result.plan);
    setStatus((s) => (s === "reveal" ? s : "reveal"));
  }, [outcome, xrayOutcome, hasHandoff]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    load();
  }, [load]);

  if (status === "missing") {
    return (
      <OnboardingLayout testId="page-today-plan" contentClassName="items-center justify-center py-10 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Let's start with a conversation.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          Today's plan builds on everything you've shared so far. When you're ready, we can begin there together.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="plan-begin-unsaid" onClick={() => navigate("/the-unsaid")}>Begin The Unsaid</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  if (status === "loading") {
    return (
      <OnboardingLayout testId="page-today-plan" contentClassName="items-center justify-center py-10 text-center">
        <div className="flex items-center gap-1.5" data-testid="plan-loading">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary/60" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
        <p className="mt-6 text-base text-muted-foreground">Let's make today a little lighter…</p>
      </OnboardingLayout>
    );
  }

  if (status === "error") {
    return (
      <OnboardingLayout testId="page-today-plan" contentClassName="items-center justify-center py-10 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Let me take another moment.</h1>
        <p className="mt-3 max-w-sm text-base leading-relaxed text-muted-foreground">
          I wasn't quite able to gather my thoughts. Give me a moment to try again.
        </p>
        <div className="mt-8 w-full max-w-[280px]">
          <PrimaryButton data-testid="plan-retry" onClick={load}>Try again</PrimaryButton>
        </div>
      </OnboardingLayout>
    );
  }

  // crisis
  if (plan?.crisis) {
    return (
      <div data-testid="page-today-plan" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
        <div className="mx-auto w-full max-w-[560px] px-6 py-14 sm:px-8 text-center" data-testid="plan-crisis">
          <Eyebrow>A moment of care</Eyebrow>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-foreground">You matter, and you deserve support.</h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/90">{plan?.focus}</p>
          <a href="tel:988" data-testid="plan-crisis-call" className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90">
            Call or text 988
          </a>
          <p className="mt-3 text-sm text-muted-foreground">US Suicide &amp; Crisis Lifeline · available 24/7</p>
          <div className="mt-8 text-center">
            <button data-testid="plan-continue" onClick={() => navigate("/future-self")}
              className="text-base font-medium text-muted-foreground transition-colors hover:text-foreground">
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // reveal
  const priorities = plan?.priorities || [];
  const watchOut = plan?.watch_out && plan.watch_out.detail ? plan.watch_out : null;
  const toggleDone = (i) => setChecked((c) => ({ ...c, [i]: !c[i] }));

  return (
    <div data-testid="page-today-plan" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto w-full max-w-[560px] px-6 py-12 sm:px-8">
        <StepProgress total={5} current={3} className="mb-10" />
        <EllieBridge feature="plan" className="mb-8" />

        <div className="text-center">
          <Eyebrow>Today's Plan</Eyebrow>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-foreground">Something small for today</h1>
        </div>

        {/* Focus */}
        <motion.div
          data-testid="plan-focus"
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-10 rounded-3xl bg-primary/[0.06] p-6 ring-1 ring-border"
        >
          <p className="eyebrow text-[11px] text-primary">Today's focus</p>
          <p className="mt-2 text-xl font-medium leading-relaxed text-foreground">{plan?.focus}</p>
        </motion.div>

        {/* Priorities — checkable next right steps */}
        <p className="mt-8 mb-3 eyebrow text-[11px] text-muted-foreground">Your next right steps</p>
        <div className="space-y-3">
          {priorities.map((p, i) => (
            <motion.button
              key={i}
              type="button"
              data-testid={`plan-priority-${i}`}
              onClick={() => toggleDone(i)}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 * (i + 1) }}
              className={`flex w-full items-start gap-4 rounded-3xl p-5 text-left ring-1 transition-all ${checked[i] ? "bg-primary/[0.06] ring-primary/40" : "bg-card ring-border hover:ring-primary/30"}`}
            >
              <span data-testid={`plan-check-${i}`} className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${checked[i] ? "border-primary bg-primary text-primary-foreground" : "border-border text-transparent"}`}>
                <Check className="h-4 w-4" strokeWidth={3} />
              </span>
              <span>
                <span className={`block text-base font-medium leading-relaxed text-foreground ${checked[i] ? "line-through opacity-60" : ""}`}>{p.action}</span>
                {p.why && <span className="mt-1.5 block text-sm leading-relaxed text-muted-foreground">{p.why}</span>}
              </span>
            </motion.button>
          ))}
        </div>

        {/* Watch-out */}
        {watchOut && (
          <motion.div
            data-testid="plan-watchout"
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 * (priorities.length + 1) }}
            className="mt-6 rounded-3xl bg-card p-6 ring-1 ring-border"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-secondary-foreground/70" style={{ color: "#B26843" }} />
              <div>
                <p className="eyebrow text-[11px]" style={{ color: "#B26843" }}>Something to watch for</p>
                {watchOut.label && <p className="mt-1.5 text-base font-medium text-foreground">{watchOut.label}</p>}
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{watchOut.detail}</p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="mt-12">
          <PrimaryButton data-testid="plan-continue" onClick={() => {
            trackSessionOnce("step_plan", "onboarding_step_completed", { step_name: "daily_battle_plan", step_number: 3 });
            navigate("/future-self");
          }}>Continue</PrimaryButton>
        </div>
      </div>
    </div>
  );
}
